import React, { useCallback, useMemo } from "react";

const BLOCKED_TAGS = ["script", "style", "iframe", "object", "embed", "link", "meta", "base", "form"];
const URL_ATTRS = ["href", "src", "action", "formaction", "xlink:href"];
const SAFE_URL_PATTERN = /^(https?:|mailto:|tel:|\/(?!\/)|#)/i;
const CORPORATE_CARD_ICON = "/Images/yool-card.svg";

const replaceCorporateCardIcons = (doc) => {
  const textNodes = Array.from(doc.body.querySelectorAll("*")).filter((node) =>
    /corporate\s+card/i.test(node.textContent || ""),
  );

  textNodes.forEach((node) => {
    let container = node;

    for (let index = 0; index < 5 && container; index += 1) {
      const icon = container.querySelector("svg, img");
      if (icon) {
        const image = doc.createElement("img");
        image.src = CORPORATE_CARD_ICON;
        image.alt = "";
        image.className = "ai-generated-card-icon-image";
        icon.replaceWith(image);
        return;
      }

      container = container.parentElement;
    }
  });
};

const sanitizeHtml = (markup) => {
  if (!markup || typeof window === "undefined" || typeof window.DOMParser !== "function") return markup || "";

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(markup, "text/html");

  BLOCKED_TAGS.forEach((tagName) => {
    doc.querySelectorAll(tagName).forEach((node) => node.remove());
  });

  doc.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();

      if (name.startsWith("on") || name === "srcdoc") {
        element.removeAttribute(attr.name);
        return;
      }

      if (URL_ATTRS.includes(name) && value && !SAFE_URL_PATTERN.test(value)) {
        element.removeAttribute(attr.name);
        return;
      }

      if (name === "target" && value === "_blank") {
        element.setAttribute("rel", "noopener noreferrer");
      }
    });
  });

  replaceCorporateCardIcons(doc);

  return doc.body.innerHTML;
};

const SummaryCard = ({ data, onRowAction, disabled }) => {
  const html = data?.Html || data?.html || "";
  const sanitizedHtml = useMemo(() => sanitizeHtml(html), [html]);

  // Handle row action button clicks (Accept/Cancel)
  const handleClick = useCallback((e) => {
    // Row actions: data-row + data-action
    const btn = e.target.closest("[data-action][data-row]");
    if (btn && !disabled) {
      const rowIdx = Number.parseInt(btn.dataset.row, 10);
      const action = btn.dataset.action;
      if (!Number.isNaN(rowIdx) && action) onRowAction?.(rowIdx, action);
      return;
    }

    if (disabled) return;

    // Validation filter clicks: data-filter
    const filterBtn = e.target.closest("[data-filter]");
    if (filterBtn) {
      const filterVal = filterBtn.dataset.filter;
      const container = e.currentTarget;
      const table = container.querySelector("#sample-transactions tbody");
      if (!table) return;

      // Toggle active state on validation cards
      const isActive = filterBtn.closest(".sc-validation-card")?.classList.contains("sc-validation-card--active");
      container.querySelectorAll(".sc-validation-card").forEach((card) => card.classList.remove("sc-validation-card--active"));

      if (isActive) {
        // Show all rows
        table.querySelectorAll("tr").forEach((row) => { row.style.display = ""; });
        // Update button text
        container.querySelectorAll(".sc-validation-link").forEach((link) => { link.textContent = "Click to edit ->"; });
      } else {
        // Filter rows by status
        filterBtn.closest(".sc-validation-card")?.classList.add("sc-validation-card--active");
        table.querySelectorAll("tr").forEach((row) => {
          const pill = row.querySelector(".sc-status-pill");
          if (!pill) return;
          const status = pill.className.replace("sc-status-pill sc-status-pill--", "").toLowerCase();
          row.style.display = status === filterVal ? "" : "none";
        });
        // Update button text
        container.querySelectorAll(".sc-validation-link").forEach((link) => { link.textContent = "Click to edit ->"; });
        filterBtn.textContent = "Show all ->";
      }
    }
  }, [onRowAction, disabled]);

  if (!sanitizedHtml) return null;

  return <div onClick={handleClick} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

export default SummaryCard;
